import React, { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { Controller, useForm } from 'react-hook-form';
import useFormPersist from 'react-hook-form-persist';
import ReCAPTCHA from 'react-google-recaptcha';
import * as jwt from 'jsonwebtoken';
import { isValidPhoneNumber } from 'react-phone-number-input';
import isEmpty from 'lodash.isempty';
import { registrationPageTwoValidationSchema } from '../../validation/registrationPageTwoValidationSchema';
import SectionLoader from '../Loader/SectionLoader';
import FormContainer from '../FormContainer/FormContainer';
import PhoneField from '../InputFields/PhoneField';
import { registerUser } from '../../store/actions/login/loginActions';
import { useBrokerCode } from '../../util/hooks/useBrokerCode';
import {
  IDENTITY_PAGE,
  TWO_FA_VERIFY_IDENTITY_PHONE,
  TWO_FA_VERIFY_IDENTITY,
  VERIFY_CHOOSE_METHOD_PAGE,
} from '../../constants/pages';
import { landingPageHelper } from '../../util/helpers/landingPageHelper';
import { selectIsLoadingByActionType } from '../../store/selectors/loadingSelectors';
import { REGISTER_USER_LOADING } from '../../store/actions/login/loginActionConstants';
import themeColors from '../../assets/styles/themeColors';
import {
  // selectIsReCaptchaEnabled,
  // selectPortalGatewayISOCodesListToIgnoreAreaCode,
  selectSettings,
} from '../../store/selectors/settingsSelectors';
import config from '../../config';
import {
  selectTokens,
  selectTwoFactorAuthenticationResponse,
} from '../../store/selectors/loginSelectors';
import useTrackingDetails from '../../util/hooks/useTrackingDetails';
import { pushInsiderFunnelEvent } from '../../util/helpers/insiderPlusEntryHelper';
import ControlledInput from '../InputFields/ControlledInput';
import { phoneNumberToObjectHelper } from '../../util/helpers/phoneNumberHelpers';
import { ALL_ZEROES_UID } from '../../constants/mockScenarios';
import {
  BaseLayoutButtonContent,
  Error,
  MobileBottomBar,
  RegisterButton,
  RegistrationWrapper,
} from './RegistrationPageOne';
import Paragraph from '../Paragraph/Paragraph';

import useWindowSize from '../../util/hooks/useIsMobileHook';
import { variables } from '../../assets/styles/variables';
import {
  removeFromSessionStorage,
  retrieveFromSessionStorage,
} from '../../util/helpers/sessionStorageHelper';
import { DISPATCH_TRACKING_DETAILS } from '../../constants/sessionStorage';

const PhoneFieldWrapper = styled.div`
  .intl-tel-input {
    input {
      background-color: ${themeColors.colorRegistrationInputBackground};
    }
  }
`;

const RegistrationPageTwo = ({ stepNumber, updateStep }) => {
  const { t } = useTranslation();
  const tokens = useSelector(selectTokens);
  const dispatch = useDispatch();
  const brokerCode = useBrokerCode();
  const history = useHistory();
  const reCaptchaRef = React.useRef();
  const brokerSettings = useSelector(selectSettings);
  const {
    IsBroker2FAActive,
    EnableGoogleReCaptchaForQuickRegistration,
  } = brokerSettings;

  const [error, setError] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [phoneKey, setPhoneKey] = useState('initial');
  const [isFirstNameFocused, setIsFirstNameFocused] = useState(false);
  const [isLastNameFocused, setIsLastNameFocused] = useState(false);
  const urlSearchParams = new URLSearchParams(window.location.search);
  const params = Object.fromEntries(urlSearchParams.entries());
  const { PromoCode, ReferrerEmail, RefereeEmail, email } = params;
  const windowSize = useWindowSize();
  const isMobile = windowSize.width < variables.breakpoints.bpLg;

  const {
    watch,
    handleSubmit,
    control,
    setValue,
    getValues,
    formState: { isValid, errors },
    trigger,
  } = useForm({
    mode: 'all',
    resolver: yupResolver(registrationPageTwoValidationSchema),
  });
  useFormPersist(
    'twostep2',
    { watch, setValue },
    {
      onDataRestored: (data) => {
        // Trigger validation and mark as touched so errors are visible on return.
        // useFormPersist restores via setValue (no shouldValidate/shouldTouch), so without this
        // the fields would have errors in state but isTouched=false, keeping errors hidden.
        setTimeout(() => {
          trigger();
          if (data?.FirstName) {
            setValue('FirstName', data.FirstName, { shouldTouch: true });
          }
          if (data?.LastName) {
            setValue('LastName', data.LastName, { shouldTouch: true });
          }
          if (data?.PhoneNumber) {
            setValue('PhoneNumber', data.PhoneNumber, { shouldTouch: true });
          }
        }, 0);
        // Force PhoneField to remount with the restored defaultValue.
        // IntlTelInput is uncontrolled and ignores defaultValue prop changes after mount.
        if (data?.PhoneNumber) {
          setPhoneKey('restored');
          if (!isValidPhoneNumber(data.PhoneNumber)) {
            setError(t('quickRegistration.errors.phone'));
          } else {
            setError('');
          }
        }
      },
    },
  );

  const handleFirstNameFocus = () => {
    setIsFirstNameFocused(true);
  };

  const handleLastNameFocus = () => {
    setIsLastNameFocused(true);
  };

  const twoFaAuthenticationResponse = useSelector(
    selectTwoFactorAuthenticationResponse,
  );
  const { TrackingDetails } = useTrackingDetails();

  const isLoading = useSelector(
    selectIsLoadingByActionType(REGISTER_USER_LOADING),
  );

  useEffect(() => {
    const emailToFill = RefereeEmail || email;
    if (emailToFill) {
      setValue('Email', emailToFill);
    }
  }, []); // eslint-disable-line

  const onSuccess = () => {
    updateStep(stepNumber);
    removeFromSessionStorage(DISPATCH_TRACKING_DETAILS);
  };

  const onSubmit = async (values, { setFieldError }) => {
    setErrorMessage('');
    if (error) return;
    let valueWithPhone = null;
    const emailPwd = retrieveFromSessionStorage('twostep1');
    const {
      Iso3CountryCode,
      AreaCode,
      LocalNumber,
    } = phoneNumberToObjectHelper(values.PhoneNumber);

    valueWithPhone = {
      ...emailPwd,
      ...values,
      PhoneNumber:
        Iso3CountryCode && AreaCode && LocalNumber
          ? {
              Iso3CountryCode,
              AreaCode,
              LocalNumber,
              PhoneNumberUid: ALL_ZEROES_UID,
              EntityPhonenumberType: 'Primary',
            }
          : null,
    };
    let token = null;
    if (EnableGoogleReCaptchaForQuickRegistration) {
      token = await reCaptchaRef.current.executeAsync();
      reCaptchaRef.current.reset();
    }
    const handleGtm = () => {
      pushInsiderFunnelEvent('registration_complete');
    };
    const newData = token
      ? {
          ...valueWithPhone,
          BrokerCode: brokerCode,
          RecaptchaToken: token,
        }
      : {
          ...valueWithPhone,
          BrokerCode: brokerCode,
        };
    let NewTrackingDetails = TrackingDetails;
    if (PromoCode) {
      NewTrackingDetails = {
        ...NewTrackingDetails,
        ReferralCode: PromoCode,
      };
    }
    if (ReferrerEmail) {
      NewTrackingDetails = {
        ...NewTrackingDetails,
        ReferrerEmail,
      };
    }

    dispatch(
      registerUser({
        BasicAccountRequest: { ...newData },
        TrackingDetails: NewTrackingDetails,
        setFieldError,
        setErrorMessage,
        onSuccess,
        handleGtm,
      }),
    );
  };

  useEffect(() => {
    function redirectClient() {
      if (!tokens.RefreshToken && !tokens.JwtToken) {
        return;
      }

      if (IsBroker2FAActive) {
        const authenticationMethodType = jwt.decode(tokens.JwtToken)
          ?.AuthenticationMethodType;

        if (tokens.JwtToken) {
          if (authenticationMethodType) {
            // Two-arg string form (not { pathname }) so history parses any
            // query string in landingPageHelper()'s result into location.search
            // instead of leaving it glued onto pathname — Insider+'s deep-link/
            // join-modal params ride in that query string and need to survive.
            return history.push(landingPageHelper(), {
              from: history.location.pathname,
            });
          }
          const { isRequired2FA } = jwt.decode(tokens.JwtToken);
          if (isRequired2FA === 'True') {
            return history.push({
              pathname: VERIFY_CHOOSE_METHOD_PAGE,
              state: {
                from: history.location.pathname,
              },
            });
          }
        }
      }

      if (tokens.RefreshToken) {
        if (IsBroker2FAActive && twoFaAuthenticationResponse) {
          if (twoFaAuthenticationResponse.AuthenticationMethodType === 2) {
            return history.replace(TWO_FA_VERIFY_IDENTITY_PHONE);
          }
          return history.replace(TWO_FA_VERIFY_IDENTITY);
        }
        return history.replace(IDENTITY_PAGE);
      }

      return history.push(landingPageHelper(), {
        from: history.location.pathname,
      });
    }
    if (tokens) {
      redirectClient();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokens]);

  return (
    <RegistrationWrapper>
      <SectionLoader isLoading={isLoading}>
        <FormContainer>
          <FormContainer twoItemsInFirstRow={!isMobile}>
            <Controller
              control={control}
              rules={{
                required: true,
              }}
              render={({ field, fieldState }) => (
                <ControlledInput
                  {...field}
                  onBlur={(e) => {
                    setIsFirstNameFocused(false);
                    field.onBlur(e);
                  }}
                  error={
                    fieldState.isTouched && !isFirstNameFocused
                      ? fieldState.error
                      : undefined
                  }
                  label={t('quickRegistration.labels.firstName')}
                  fontSize="14px"
                  height={54}
                  autoComplete="given-name"
                  placeholder={t(
                    'quickRegistration.twoStep.rightPane.firstNamePlaceholder',
                  )}
                  handleFocus={handleFirstNameFocus}
                  data-cy="input-first-name"
                />
              )}
              name="FirstName"
            />
            <Controller
              control={control}
              render={({ field, fieldState }) => (
                <ControlledInput
                  {...field}
                  onBlur={(e) => {
                    setIsLastNameFocused(false);
                    field.onBlur(e);
                  }}
                  error={
                    fieldState.isTouched && !isLastNameFocused
                      ? fieldState.error
                      : undefined
                  }
                  label={t('quickRegistration.labels.lastName')}
                  fontSize="14px"
                  height={54}
                  autoComplete="family-name"
                  placeholder={t(
                    'quickRegistration.twoStep.rightPane.lastNamePlaceholder',
                  )}
                  handleFocus={handleLastNameFocus}
                  data-cy="input-last-name"
                />
              )}
              name="LastName"
            />
          </FormContainer>
          <FormContainer>
            <Controller
              control={control}
              render={({ field, fieldState }) => (
                <PhoneFieldWrapper>
                  <PhoneField
                    key={phoneKey}
                    {...field}
                    optional={false}
                    name="PhoneNumber"
                    label={t('quickRegistration.twoStep.rightPane.phone')}
                    setValue={setValue}
                    setError={setError}
                    defaultValue={getValues('PhoneNumber')}
                    dataCy="input-phone"
                    error={
                      fieldState?.isTouched
                        ? fieldState?.error?.message
                        : undefined
                    }
                    note={t(
                      'quickRegistration.twoStep.rightPane.phoneFieldNote',
                    )}
                    noteFontSize={14}
                    color={themeColors.colorInfoTitle}
                    hideErrorOnFocus
                    fontSize="14px"
                  />
                </PhoneFieldWrapper>
              )}
              name="PhoneNumber"
            />
            {PromoCode && (
              <Controller
                control={control}
                render={({ field, fieldState }) => (
                  <ControlledInput
                    {...field}
                    {...fieldState}
                    label={t('quickRegistration.labels.promoCode')}
                    autoComplete="promo-code"
                    data-cy="input-promo-code"
                    value={PromoCode}
                    disabled
                  />
                )}
              />
            )}
          </FormContainer>
        </FormContainer>
        {errorMessage && <Error>{errorMessage}</Error>}
        <MobileBottomBar>
          <BaseLayoutButtonContent fullWidth>
            <RegisterButton
              type="submit"
              data-cy="button-continue"
              onClick={handleSubmit(onSubmit)}
              disabled={!isValid || !isEmpty(errors)}
            >
              {t('quickRegistration.twoStep.rightPane.createAccountButtonText')}
            </RegisterButton>
            <Paragraph
              fontSize={16}
              usePx
              color={themeColors.colorTextPrimary}
              style={{ textAlign: 'center' }}
            >
              {t('quickRegistration.twoStep.leftPane.clickAboveText')}
            </Paragraph>
          </BaseLayoutButtonContent>
        </MobileBottomBar>
        {EnableGoogleReCaptchaForQuickRegistration && (
          <ReCAPTCHA
            ref={reCaptchaRef}
            sitekey={config.recaptcha.siteKey}
            size="invisible"
          />
        )}
      </SectionLoader>
    </RegistrationWrapper>
  );
};

RegistrationPageTwo.propTypes = {
  history: PropTypes.shape({
    replace: PropTypes.func,
  }),
  stepNumber: PropTypes.number,
  updateStep: PropTypes.func,
};

export default RegistrationPageTwo;
